import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface ProjectDocument {
  id: number;
  project_id: number;
  filename: string;
  original_name: string;
  mimetype: string;
  size_bytes: number;
  uploaded_by: number | null;
  uploaded_at: string;
  uploader_name: string | null;
}

export function useDocuments(projectId: number | undefined) {
  return useQuery({
    queryKey: ['documents', projectId],
    queryFn: async () => {
      const { data } = await api.get<{ items: ProjectDocument[] }>(
        `/projects/${projectId}/documents`,
      );
      return data.items;
    },
    enabled: projectId !== undefined,
  });
}

export function useUploadDocument(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post<ProjectDocument>(
        `/projects/${projectId}/documents`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', projectId] }),
  });
}

export function useDeleteDocument(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/documents/${id}`);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', projectId] }),
  });
}

export async function downloadDocument(doc: ProjectDocument) {
  const response = await api.get(`/documents/${doc.id}`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.original_name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
